import { useTranslation } from 'react-i18next';

/**
 * One glyph per channel, never reused (redesign, A00-05): a Facebook Page's
 * posts get the "f", not Messenger's bolt. The name is always there for screen
 * readers, so the channel is never told by colour alone.
 */
export type Channel = 'instagram' | 'messenger' | 'facebook';

export type ChannelGlyphSize = 14 | 16 | 28;

const BACKGROUNDS: Record<Channel, string> = {
  instagram: 'bg-channel-instagram',
  messenger: 'bg-channel-messenger rounded-full',
  facebook: 'bg-channel-facebook rounded-full',
};

export interface ChannelGlyphProps {
  channel: Channel;
  size?: ChannelGlyphSize;
  className?: string;
}

export function ChannelGlyph({ channel, size = 16, className = '' }: ChannelGlyphProps) {
  const { t } = useTranslation();
  const inner = size === 28 ? 16 : size - 6;
  // The large tile is a rounded square for every channel, as on the DS board.
  const shape = size === 28 ? 'rounded-ds-md' : channel === 'instagram' ? 'rounded-[5px]' : '';
  return (
    <span
      className={`relative inline-grid shrink-0 place-items-center text-white ${BACKGROUNDS[channel]} ${shape} ${className}`}
      style={{ width: size, height: size }}
    >
      <span className="sr-only">{t(`channel.${channel}`)}</span>
      {channel === 'instagram' ? (
        <svg aria-hidden width={inner} height={inner} viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.3">
          <rect x="1.5" y="1.5" width="7" height="7" rx="2" />
          <circle cx="5" cy="5" r="1.6" />
        </svg>
      ) : null}
      {channel === 'messenger' ? (
        <svg aria-hidden width={inner} height={inner} viewBox="0 0 10 10">
          <path d="M2 6.2 4.2 4l1.3 1.2L8 3.8 5.8 6 4.5 4.8z" fill="currentColor" />
        </svg>
      ) : null}
      {channel === 'facebook' ? (
        <span aria-hidden style={{ fontSize: Math.round(size * 0.62), fontWeight: 700, lineHeight: 1 }}>
          f
        </span>
      ) : null}
    </span>
  );
}
